import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { computeGameDeviceHash } from "@/lib/gameDeviceFingerprint";

export interface GamePlayer {
  id: string;
  user_id: string;
  display_name: string;
  avatar_url: string | null;
  email?: string | null;
  virtual_balance: number;
  total_spent: number;
  total_earned: number;
  created_at: string;
}

// L1 anti-fraud: register device and check for multi-account abuse.
async function runDeviceFraudCheck(): Promise<{ eligible: boolean; reason?: string }> {
  try {
    const deviceHash = await computeGameDeviceHash();
    const { data, error } = await supabase.functions.invoke("game-signup-fraud-check", {
      body: { device_hash: deviceHash },
    });
    if (error) {
      console.warn("Fraud check error (allowing):", error);
      return { eligible: true };
    }
    return { eligible: !!data?.eligible, reason: data?.reason };
  } catch (e) {
    console.warn("Fraud check exception (allowing):", e);
    return { eligible: true };
  }
}

function isWalletOnlyGameSession(session: any) {
  const email = session?.user?.email?.toLowerCase?.() || "";
  const walletAddress = session?.user?.user_metadata?.wallet_address;
  return !!walletAddress || email.endsWith("@wallet.local");
}

export function useGameAuth() {
  const [session, setSession] = useState<any>(null);
  const [player, setPlayer] = useState<GamePlayer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Capture referral code from URL on ANY page load (works on any /game* route).
    // Persist in localStorage + sessionStorage + cookie so the code survives:
    //  - in-app browser → system browser handoff (cookie on .flownft.org)
    //  - private/incognito sessions (sessionStorage fallback)
    //  - localStorage being cleared between visits
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (ref && ref.trim()) {
        const code = ref.trim();
        try { localStorage.setItem("flow_game_referral", code); } catch {}
        try { sessionStorage.setItem("flow_game_referral", code); } catch {}
        try {
          const host = window.location.hostname;
          const domainAttr = host.endsWith("flownft.org") ? "; domain=.flownft.org" : "";
          document.cookie = `flow_game_referral=${encodeURIComponent(code)}; path=/; max-age=${60 * 24 * 60 * 60}; SameSite=Lax${domainAttr}`;
        } catch {}
      }
    } catch {}

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (isWalletOnlyGameSession(session)) {
        toast.error("Game Demo supports email/password accounts only.");
        void supabase.auth.signOut();
        setSession(null);
        setPlayer(null);
        setLoading(false);
        return;
      }
      setSession(session);
      if (session?.user) {
        setTimeout(() => fetchOrCreatePlayer(session.user.id, session.user.email || "Player"), 0);
      } else {
        setPlayer(null);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (isWalletOnlyGameSession(session)) {
        toast.error("Game Demo supports email/password accounts only.");
        void supabase.auth.signOut();
        setSession(null);
        setPlayer(null);
        setLoading(false);
        return;
      }
      setSession(session);
      if (session?.user) {
        fetchOrCreatePlayer(session.user.id, session.user.email || "Player");
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchOrCreatePlayer = async (userId: string, email: string) => {
    try {
      const { data: existing } = await supabase
        .from("game_players")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      let playerRow: any = existing;
      let isNewPlayer = false;

      if (!playerRow) {
        const displayName = email.split("@")[0];
        const { data: { user } } = await supabase.auth.getUser();
        const metaName = user?.user_metadata?.display_name;
        const finalName = metaName || displayName;
        const { data: created, error } = await supabase
          .from("game_players")
          .insert({ user_id: userId, display_name: finalName })
          .select("*")
          .single();
        if (error) throw error;
        playerRow = created;
        isNewPlayer = true;
      }

      // L1 anti-fraud: register device + check for multi-account abuse.
      // FIX: only run on initial signup. Running on every login was logging users
      // out whenever IP/device hash drifted (different network, browser update, etc.).
      if (isNewPlayer) {
        const fraud = await runDeviceFraudCheck();
        if (!fraud.eligible) {
          const msg = fraud.reason === "device_already_used"
            ? "This device is already linked to another Game account. Multiple accounts are not allowed."
            : fraud.reason === "invalid_fingerprint"
            ? "Device verification failed. Please try a different browser."
            : "Account verification failed. Please try again.";
          toast.error(msg);
          // Delete the freshly created player to avoid orphan rows.
          if (playerRow?.id) {
            await supabase.from("game_players").delete().eq("id", playerRow.id);
          }
          await supabase.auth.signOut();
          setPlayer(null);
          setSession(null);
          setLoading(false);
          return;
        }
      }

      setPlayer({
        ...(playerRow as unknown as GamePlayer),
        email,
      });

      // Check for pending referral code (only for new players).
      // Read from ALL sources (localStorage, sessionStorage, cookie) so the code
      // is found even if one storage was cleared between click and signup.
      if (isNewPlayer) {
        const readCookie = (name: string): string | null => {
          try {
            const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
            return m ? decodeURIComponent(m[1]) : null;
          } catch { return null; }
        };
        const pendingRef =
          (() => { try { return localStorage.getItem("flow_game_referral"); } catch { return null; } })() ||
          (() => { try { return sessionStorage.getItem("flow_game_referral"); } catch { return null; } })() ||
          readCookie("flow_game_referral");

        if (pendingRef && playerRow) {
          // Retry up to 3 times on transient errors. Only clear storage on a
          // DEFINITIVE outcome (success OR explicit non-retryable failure from RPC).
          // If everything fails, KEEP the code so a future retry (or manual recovery)
          // can still credit the referrer.
          let redeemed = false;
          let definitiveFailure = false;
          for (let attempt = 0; attempt < 3 && !redeemed && !definitiveFailure; attempt++) {
            try {
              const { data: result, error: rpcErr } = await supabase.rpc("redeem_game_referral", {
                p_referral_code: pendingRef,
                p_new_player_id: playerRow.id,
              });
              if (rpcErr) {
                console.warn(`Referral redeem RPC error (attempt ${attempt + 1}):`, rpcErr);
                await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
                continue;
              }
              const res = result as any;
              if (res?.success) {
                redeemed = true;
                toast.success(`Referral applied! ${res.referrer_name} earned $10`);
              } else {
                // RPC responded but rejected (e.g. invalid code, already used, self-referral)
                definitiveFailure = true;
                console.info("Referral not applied:", res?.reason || "unknown");
              }
            } catch (e) {
              console.warn(`Referral redeem exception (attempt ${attempt + 1}):`, e);
              await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
            }
          }

          if (redeemed || definitiveFailure) {
            // Clear from every storage layer
            try { localStorage.removeItem("flow_game_referral"); } catch {}
            try { sessionStorage.removeItem("flow_game_referral"); } catch {}
            try {
              const host = window.location.hostname;
              const domainAttr = host.endsWith("flownft.org") ? "; domain=.flownft.org" : "";
              document.cookie = `flow_game_referral=; path=/; max-age=0; SameSite=Lax${domainAttr}`;
            } catch {}
          } else {
            console.warn("Referral redeem failed after retries — code preserved for future retry");
          }
        }
      }
    } catch (err: any) {
      console.error("Game player error:", err);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } },
    });
    if (error) {
      toast.error(error.message);
      return false;
    }
    // After auto-confirm, onAuthStateChange will fire and create the player
    toast.success("Account created! Welcome to FLOW Game 🎮");
    return true;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(error.message);
      return false;
    }
    return true;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setPlayer(null);
    setSession(null);
  };

  const refreshPlayer = async () => {
    if (!session?.user) return;
    const { data } = await supabase
      .from("game_players")
      .select("*")
      .eq("user_id", session.user.id)
      .single();
    if (data) {
      setPlayer({
        ...(data as unknown as GamePlayer),
        email: session.user.email || null,
      });
    }
  };

  const updateDisplayName = async (newName: string) => {
    if (!player) return false;
    const trimmed = newName.trim();
    if (!trimmed || trimmed.length < 2 || trimmed.length > 30) {
      toast.error("Name must be 2-30 characters");
      return false;
    }
    const { error } = await supabase
      .from("game_players")
      .update({ display_name: trimmed })
      .eq("id", player.id);
    if (error) {
      toast.error(error.message);
      return false;
    }
    setPlayer({ ...player, display_name: trimmed });
    toast.success("Display name updated!");
    return true;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/game/reset-password`,
    });
    if (error) {
      toast.error(error.message);
      return false;
    }
    toast.success("Password reset email sent!");
    return true;
  };

  return { session, player, loading, signUp, signIn, signOut, refreshPlayer, updateDisplayName, resetPassword };
}
